import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, Eye, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { api, fmtBR, fmtWhatsApp, formatApiError, WEEKDAY_NAMES } from "@/lib/api";

const STATUS_LABELS = { pendente: "Pendente", aprovada: "Aprovada", rejeitada: "Rejeitada" };
const HISTORY_LABELS = {
  criacao: "Criação",
  envio_duplicado: "Envio duplicado absorvido",
  aprovacao: "Aprovação",
  rejeicao: "Rejeição",
};
const RELATIONSHIP_LABELS = { responsavel: "Responsável", pai: "Pai", mae: "Mãe", avo: "Avó/Avô", tutor: "Tutor(a)", outro: "Outro" };

export const RequestStatusBadge = ({ status }) => (
  <Badge
    variant={status === "aprovada" ? "default" : status === "rejeitada" ? "destructive" : "secondary"}
    className={status === "aprovada" ? "bg-primary text-primary-foreground" : ""}
    data-testid={`request-status-${status}`}
  >
    {STATUS_LABELS[status] || status}
  </Badge>
);

function requestAge(req) {
  if (!req?.student?.birth_date) return null;
  const b = new Date(`${req.student.birth_date}T00:00:00`);
  const now = new Date();
  let a = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) a -= 1;
  return a;
}

function fmtSlot(slot) {
  if (!slot) return "Sem preferência";
  return `${slot.weekday_name}, ${slot.start_time}–${slot.end_time}`;
}

function ApproveDialog({ request, onClose, onDone }) {
  const [withEnrollment, setWithEnrollment] = useState(false);
  const [lookups, setLookups] = useState({ teachers: [], rooms: [] });
  const [form, setForm] = useState({
    teacher_id: "", room_id: "",
    weekday: request.preferred_slot ? String(request.preferred_slot.weekday) : "",
    start_time: request.preferred_slot?.start_time || "",
    start_date: new Date().toISOString().slice(0, 10),
  });
  const [matches, setMatches] = useState(null);
  const [studentId, setStudentId] = useState("");
  const [confirmNew, setConfirmNew] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/teachers"), api.get("/rooms")])
      .then(([t, r]) => setLookups({
        teachers: t.data.filter((x) => x.active),
        rooms: r.data.filter((x) => x.active),
      }))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  const compatibleTeachers = useMemo(
    () => lookups.teachers.filter((t) => (t.course_ids || []).includes(request.course_id)),
    [lookups.teachers, request.course_id]
  );

  const enrollmentReady =
    form.teacher_id && form.room_id && form.weekday !== "" && form.start_time && form.start_date;

  const submit = async () => {
    setSaving(true);
    setConflicts([]);
    try {
      const payload = {};
      if (studentId) payload.student_id = studentId;
      if (confirmNew) payload.confirm_new_student = true;
      if (withEnrollment) {
        payload.teacher_id = form.teacher_id;
        payload.room_id = form.room_id;
        payload.weekday = Number(form.weekday);
        payload.start_time = form.start_time;
        payload.start_date = form.start_date;
      }
      const { data } = await api.post(`/enrollment-requests/${request.id}/approve`, payload);
      toast.success(
        data.enrollment_id
          ? `Solicitação aprovada: aluno ${data.student_reused ? "vinculado" : "criado"} e matrícula concluída (${data.lessons_generated} aulas geradas).`
          : `Solicitação aprovada: aluno ${data.student_reused ? "vinculado" : "criado"}. Conclua a matrícula pelo fluxo de Matrículas.`
      );
      onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail && !Array.isArray(detail) && detail.matches) {
        setMatches(detail.matches);
      } else if (err?.response?.status === 409 && Array.isArray(detail)) {
        setConflicts(detail);
      } else {
        setConflicts([formatApiError(err)]);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="request-approve-dialog">
        <DialogHeader>
          <DialogTitle>Aprovar solicitação — {request.student.full_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <p className="border border-border bg-zinc-50 p-3 text-sm" data-testid="request-approve-summary">
            Ao confirmar, o aluno oficial será criado{request.guardian ? " e o responsável será criado/vinculado" : ""}.
            Nenhuma cobrança ou conta de Portal será gerada.
          </p>

          {matches && (
            <div className="border border-amber-500/50 bg-amber-50 p-3" data-testid="request-approve-matches">
              <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                <AlertTriangle size={16} /> Possível aluno já cadastrado
              </p>
              <div className="mt-2 space-y-2">
                {matches.map((m) => (
                  <div key={m.id} className="flex items-center justify-between gap-2 border border-border bg-white p-2 text-sm">
                    <span>
                      {m.full_name} {m.age != null && `(${m.age} anos)`} · {fmtBR(m.birth_date)}
                    </span>
                    <Button
                      size="sm"
                      variant={studentId === m.id ? "default" : "outline"}
                      onClick={() => { setStudentId(m.id); setConfirmNew(false); }}
                      data-testid={`request-approve-use-student-${m.id}`}
                    >
                      {studentId === m.id ? "Selecionado" : "Usar este aluno"}
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                size="sm"
                variant={confirmNew ? "default" : "outline"}
                className="mt-2"
                onClick={() => { setConfirmNew(true); setStudentId(""); }}
                data-testid="request-approve-confirm-new-student"
              >
                Criar novo aluno mesmo assim
              </Button>
            </div>
          )}

          <div className="flex items-start gap-3 border border-border p-3">
            <Checkbox
              id="approve-with-enrollment"
              checked={withEnrollment}
              onCheckedChange={(v) => setWithEnrollment(v === true)}
              data-testid="request-approve-enrollment-toggle"
            />
            <Label htmlFor="approve-with-enrollment" className="cursor-pointer text-sm font-normal leading-relaxed">
              Concluir a matrícula agora ({request.course_name} · {request.modality_name}).
              Se desmarcado, somente o aluno{request.guardian ? " e o responsável" : ""} será(ão) criado(s)
              e a matrícula ficará para o fluxo normal.
            </Label>
          </div>

          {withEnrollment && (
            <div className="space-y-4 border border-border p-4" data-testid="request-approve-enrollment-fields">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Professor *</Label>
                  <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                    <SelectTrigger data-testid="request-approve-teacher-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {compatibleTeachers.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Sala *</Label>
                  <Select value={form.room_id} onValueChange={(v) => setForm({ ...form, room_id: v })}>
                    <SelectTrigger data-testid="request-approve-room-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {lookups.rooms.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Dia da semana *</Label>
                  <Select value={form.weekday} onValueChange={(v) => setForm({ ...form, weekday: v })}>
                    <SelectTrigger data-testid="request-approve-weekday-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {WEEKDAY_NAMES.map((n, i) => (
                        <SelectItem key={i} value={String(i)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Horário de início *</Label>
                  <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="request-approve-start-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Data de início *</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="request-approve-date-input" />
              </div>
              {request.preferred_slot && (
                <p className="text-xs text-muted-foreground" data-testid="request-approve-preference-note">
                  Preferência do candidato: {fmtSlot(request.preferred_slot)} (pré-preenchida — confirme a disponibilidade).
                </p>
              )}
            </div>
          )}

          {conflicts.length > 0 && (
            <div className="border border-destructive/50 bg-destructive/10 p-3" data-testid="request-approve-conflicts">
              <p className="flex items-center gap-2 text-sm font-semibold text-destructive">
                <AlertTriangle size={16} /> Não foi possível aprovar
              </p>
              <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-destructive">
                {conflicts.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={onClose} data-testid="request-approve-cancel">Cancelar</Button>
            <Button
              onClick={submit}
              disabled={saving || (withEnrollment && !enrollmentReady) || (!!matches && !studentId && !confirmNew)}
              className="bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
              data-testid="request-approve-confirm-button"
            >
              <Check size={16} className="mr-2" />
              {saving ? "Aprovando..." : "Confirmar aprovação"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function EnrollmentRequestsPanel() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pendente");
  const [detail, setDetail] = useState(null);
  const [approveFor, setApproveFor] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const load = async () => {
    const params = statusFilter !== "todas" ? { status: statusFilter } : {};
    const { data } = await api.get("/enrollment-requests", { params });
    setItems(data);
  };

  useEffect(() => {
    load().catch((err) => toast.error(formatApiError(err)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const openDetail = async (req) => {
    try {
      const { data } = await api.get(`/enrollment-requests/${req.id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reject = async () => {
    setRejecting(true);
    try {
      await api.post(`/enrollment-requests/${rejectFor.id}/reject`, { reason: rejectReason.trim() || null });
      toast.success("Solicitação rejeitada. O registro permanece para auditoria.");
      setRejectFor(null);
      setRejectReason("");
      setDetail(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRejecting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="requests-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Solicitações recebidas pelo link público de matrícula. A aprovação cria o aluno oficial; a matrícula pode
          ser concluída no mesmo fluxo ou posteriormente.
        </p>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="requests-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pendente">Pendentes</SelectItem>
            <SelectItem value="aprovada">Aprovadas</SelectItem>
            <SelectItem value="rejeitada">Rejeitadas</SelectItem>
            <SelectItem value="todas">Todas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider">Aluno</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider md:table-cell">Responsável / Contato</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Curso</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider lg:table-cell">Preferência</TableHead>
              <TableHead className="hidden text-xs uppercase tracking-wider sm:table-cell">Data</TableHead>
              <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="p-6 text-sm text-muted-foreground" data-testid="requests-empty">
                  Nenhuma solicitação encontrada.
                </TableCell>
              </TableRow>
            )}
            {items.map((r) => (
              <TableRow key={r.id} className="transition-colors hover:bg-zinc-50" data-testid={`request-row-${r.id}`}>
                <TableCell className="font-medium">
                  {r.student.full_name}
                  {requestAge(r) != null && <span className="ml-1 text-xs text-muted-foreground">({requestAge(r)} anos)</span>}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  {r.guardian
                    ? `${r.guardian.full_name} · ${fmtWhatsApp(r.guardian.whatsapp)}`
                    : r.student_contact
                      ? fmtWhatsApp(r.student_contact.whatsapp)
                      : "—"}
                </TableCell>
                <TableCell>{r.course_name} · {r.modality_name}</TableCell>
                <TableCell className="hidden lg:table-cell">{fmtSlot(r.preferred_slot)}</TableCell>
                <TableCell className="hidden sm:table-cell">{fmtBR((r.created_at || "").slice(0, 10))}</TableCell>
                <TableCell><RequestStatusBadge status={r.status} /></TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" title="Detalhes" onClick={() => openDetail(r)} data-testid={`request-view-${r.id}`}>
                    <Eye size={16} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="request-detail-dialog">
          <DialogHeader>
            <DialogTitle>Solicitação — {detail?.student?.full_name}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Aluno</p><p className="font-medium">{detail.student.full_name}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Nascimento</p><p className="font-medium">{fmtBR(detail.student.birth_date)} ({requestAge(detail)} anos)</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Status</p><RequestStatusBadge status={detail.status} /></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Curso</p><p className="font-medium">{detail.course_name}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Modalidade</p><p className="font-medium">{detail.modality_name}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Preferência</p><p className="font-medium">{fmtSlot(detail.preferred_slot)}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Enviada em</p><p className="font-medium">{new Date(detail.created_at).toLocaleString("pt-BR")}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Origem</p><p className="font-medium">{detail.origin}</p></div>
              </div>

              {detail.student.notes && (
                <div className="text-sm">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Observações do candidato</p>
                  <p className="mt-1 border border-border bg-zinc-50 p-2" data-testid="request-detail-notes">{detail.student.notes}</p>
                </div>
              )}

              {detail.guardian && (
                <div className="text-sm" data-testid="request-detail-guardian">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Responsável</p>
                  <p className="mt-1 font-medium">
                    {detail.guardian.full_name} · {RELATIONSHIP_LABELS[detail.guardian.relationship] || detail.guardian.relationship}
                  </p>
                  <p className="text-muted-foreground">{fmtWhatsApp(detail.guardian.whatsapp)} · {detail.guardian.email}</p>
                </div>
              )}
              {!detail.guardian && detail.student_contact && (
                <div className="text-sm" data-testid="request-detail-contact">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Contato do aluno</p>
                  <p className="mt-1 font-medium">{fmtWhatsApp(detail.student_contact.whatsapp)}{detail.student_contact.email ? ` · ${detail.student_contact.email}` : ""}</p>
                </div>
              )}

              {detail.status === "rejeitada" && detail.rejection_reason && (
                <div className="text-sm" data-testid="request-detail-rejection">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Motivo interno da rejeição</p>
                  <p className="mt-1 border border-border bg-zinc-50 p-2">{detail.rejection_reason}</p>
                </div>
              )}
              {detail.status === "aprovada" && (
                <div className="text-sm" data-testid="request-detail-approval">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Resultado da aprovação</p>
                  <p className="mt-1 font-medium">
                    Aluno {detail.student_id ? "registrado" : "—"} · {detail.enrollment_id ? "Matrícula concluída" : "Matrícula pendente de conclusão manual"}
                  </p>
                </div>
              )}

              {detail.status === "pendente" && (
                <div className="flex flex-col gap-2 sm:flex-row" data-testid="request-detail-actions">
                  <Button
                    className="flex-1 bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
                    onClick={() => setApproveFor(detail)}
                    data-testid="request-approve-button"
                  >
                    <Check size={16} className="mr-2" /> Aprovar
                  </Button>
                  <Button variant="destructive" className="flex-1" onClick={() => setRejectFor(detail)} data-testid="request-reject-button">
                    <X size={16} className="mr-2" /> Rejeitar
                  </Button>
                </div>
              )}

              {detail.history?.length > 0 && (
                <div>
                  <h3 className="font-heading text-base font-semibold">Histórico</h3>
                  <div className="mt-2 space-y-2" data-testid="request-history-list">
                    {detail.history.map((h) => (
                      <div key={h.id} className="border border-border p-2 text-xs">
                        <span className="font-semibold">{HISTORY_LABELS[h.action] || h.action}</span>
                        <span className="text-muted-foreground"> · {h.user_name} · {new Date(h.created_at).toLocaleString("pt-BR")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {approveFor && (
        <ApproveDialog
          request={approveFor}
          onClose={() => setApproveFor(null)}
          onDone={() => { setApproveFor(null); setDetail(null); load(); }}
        />
      )}

      <Dialog open={!!rejectFor} onOpenChange={() => setRejectFor(null)}>
        <DialogContent data-testid="request-reject-dialog">
          <DialogHeader>
            <DialogTitle>Rejeitar solicitação — {rejectFor?.student?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              A solicitação permanecerá registrada para auditoria. O motivo é interno e não é exposto ao candidato.
            </p>
            <div className="space-y-2">
              <Label>Motivo interno (opcional)</Label>
              <Textarea
                rows={3}
                maxLength={500}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                data-testid="request-reject-reason-input"
              />
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={() => setRejectFor(null)} data-testid="request-reject-cancel">Cancelar</Button>
              <Button variant="destructive" disabled={rejecting} onClick={reject} data-testid="request-reject-confirm-button">
                {rejecting ? "Rejeitando..." : "Confirmar rejeição"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
